import { useEffect, useRef, useState } from 'react';
import { ProductLogo } from '@/components/product-logo';
import { cn } from '@/lib/utils';
import type { LogoHandle } from './logo3d/scene';

const hasWebGL = () => {
  try {
    return Boolean(document.createElement('canvas').getContext('webgl2'));
  } catch {
    return false;
  }
};

/**
 * The product mark as an animated 3D object (three.js, loaded on demand) for always-dark surfaces
 * such as the sign-in screen. The flat logo shows while the model loads and stays if WebGL2 is
 * missing or loading fails. A click spins it.
 */
export function Logo3D({ className }: { className?: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const logo = useRef<LogoHandle | null>(null);
  const [mode, setMode] = useState<'loading' | '3d' | 'flat'>(() =>
    hasWebGL() ? 'loading' : 'flat',
  );

  useEffect(() => {
    if (mode === 'flat') return;
    let cancelled = false;
    import('./logo3d/scene')
      .then(({ createLogo }) => {
        if (cancelled || !canvasRef.current) return;
        logo.current = createLogo(canvasRef.current);
        return logo.current.ready.then(() => !cancelled && setMode('3d'));
      })
      .catch(() => !cancelled && setMode('flat'));
    return () => {
      cancelled = true;
      logo.current?.dispose();
      logo.current = null;
    };
    // Created once; `mode` only changes from here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={cn('relative flex items-center justify-center', className)}>
      {/* Violet halo behind the mark so the black body separates from the page. */}
      <div
        className="pointer-events-none absolute inset-[18%] rounded-full bg-[radial-gradient(circle,rgb(139_92_246/0.35),transparent_70%)] blur-2xl"
        aria-hidden
      />
      {mode !== '3d' && (
        <ProductLogo
          tone="onDark"
          className="absolute h-14 drop-shadow-[0_4px_20px_rgb(255_255_255/0.2)]"
        />
      )}
      {mode !== 'flat' && (
        <canvas
          ref={canvasRef}
          onClick={() => logo.current?.spin()}
          className={cn(
            'relative block size-full cursor-pointer transition-opacity duration-700',
            mode === '3d' ? 'opacity-100' : 'opacity-0',
          )}
          aria-hidden
        />
      )}
    </div>
  );
}
